import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, CalendarDays, Send, Loader2 } from "lucide-react";
import { fadeUp, stagger, viewport } from "./anim";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "843-731-5155";

const loadCalendly = () =>
  new Promise((resolve) => {
    if (window.Calendly) return resolve();
    if (!document.querySelector("link[href*='calendly']")) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(link);
    }
    let s = document.querySelector("script[src*='calendly']");
    if (s) {
      s.addEventListener("load", () => resolve());
      if (window.Calendly) resolve();
      return;
    }
    s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    s.onload = () => resolve();
    document.body.appendChild(s);
  });

export const Booking = () => {
  const [cfg, setCfg] = useState({ calendly_url: "", phone: PHONE });
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const calRef = useRef(null);
  const inited = useRef(false);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!cfg.calendly_url || inited.current || !calRef.current) return;
    inited.current = true;
    loadCalendly().then(() => {
      if (window.Calendly && calRef.current) {
        calRef.current.innerHTML = "";
        window.Calendly.initInlineWidget({
          url: `${cfg.calendly_url}?hide_gdpr_banner=1`,
          parentElement: calRef.current,
        });
      }
    });
  }, [cfg.calendly_url]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Got it! Melanie will reach out personally soon.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      toast.error(`Something went wrong. Call or text ${PHONE}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="booking-section" className="relative py-24 sm:py-32 overflow-hidden" style={{ background: "#0b0912" }}>
      <div className="absolute inset-0 hero-grid opacity-50" />
      <div className="absolute -top-20 right-1/4 w-[32rem] h-[32rem] rounded-full opacity-30 animate-blob" style={{ background: "radial-gradient(circle,#8B5CF6,transparent 65%)", filter: "blur(90px)" }} />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div initial="hidden" whileInView="show" viewport={viewport} variants={stagger} className="max-w-2xl mb-14">
          <motion.div variants={fadeUp} className="font-mono-accent text-xs tracking-[0.25em] uppercase text-fuchsia-400 mb-4">
            Let's Talk
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
            Ready to see what AI could actually{" "}
            <span className="text-gradient">do for your business?</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-slate-300/90 text-lg">
            Grab a time below, or send a note — call/text us directly anytime.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Calendly embed placeholder */}
          <motion.div initial="hidden" whileInView="show" viewport={viewport} variants={fadeUp}
            data-testid="booking-calendly-container"
            className="glass-dark rounded-3xl p-8 flex flex-col">
            <div className="inline-flex items-center gap-2 font-mono-accent text-[10px] tracking-[0.2em] uppercase text-fuchsia-300 mb-6">
              <CalendarDays size={14} /> Book a free consultation
            </div>
            <div
              ref={calRef}
              data-testid="calendly-inline-widget"
              className="flex-1 rounded-2xl overflow-hidden bg-white min-h-[34rem] flex items-center justify-center"
              style={{ minWidth: "280px" }}
            >
              <div className="flex flex-col items-center text-center px-6">
                <Loader2 size={26} className="text-fuchsia-500 animate-spin mb-3" />
                <p className="text-slate-500 text-sm">Loading the scheduler…</p>
              </div>
            </div>
            <a
              href={cfg.calendly_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-calendly-fallback"
              className="mt-3 text-center text-sm text-fuchsia-300 hover:text-fuchsia-200 underline underline-offset-4"
            >
              Trouble seeing the calendar? Open the scheduler in a new tab →
            </a>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <a href={`tel:${PHONE.replace(/-/g, "")}`} data-testid="booking-phone-link" className="flex items-center gap-3 rounded-xl px-4 py-3 bg-white/5 border border-white/10 hover:border-fuchsia-400/40 transition-colors">
                <Phone size={18} className="text-fuchsia-400" />
                <span className="text-white font-medium">{PHONE}</span>
              </a>
              <a href="mailto:info@activatethenelevate.com" data-testid="booking-email-link" className="flex items-center gap-3 rounded-xl px-4 py-3 bg-white/5 border border-white/10 hover:border-fuchsia-400/40 transition-colors">
                <Mail size={18} className="text-fuchsia-400" />
                <span className="text-white font-medium text-sm truncate">info@activatethenelevate.com</span>
              </a>
            </div>
          </motion.div>

          {/* Contact form */}
          <motion.form initial="hidden" whileInView="show" viewport={viewport} variants={fadeUp}
            onSubmit={submit} data-testid="booking-form"
            className="glass-dark rounded-3xl p-8">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name*" testid="contact-name" value={form.name} onChange={update("name")} placeholder="Jane Doe" />
              <Field label="Email*" testid="contact-email" type="email" value={form.email} onChange={update("email")} placeholder="jane@company.com" />
              <Field label="Phone" testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder="(843) 000-0000" />
              <Field label="Company" testid="contact-company" value={form.company} onChange={update("company")} placeholder="Company name" />
            </div>
            <div className="mt-4">
              <label className="block text-xs font-mono-accent uppercase tracking-wider text-slate-400 mb-2">What do you want to build?</label>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={update("message")}
                rows={4}
                placeholder="Tell us a little about your business and where AI could help…"
                className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:border-fuchsia-400/60 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/20 transition"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="booking-form-submit-button"
              className="btn-glow mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-semibold text-white disabled:opacity-60"
              style={{ background: "linear-gradient(100deg,#FF4FD8,#E933C6 55%,#8B5CF6)" }}
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
              {sending ? "Sending…" : "Send & Get Started"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, value, onChange, placeholder, type = "text" }) => (
  <div>
    <label className="block text-xs font-mono-accent uppercase tracking-wider text-slate-400 mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:border-fuchsia-400/60 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/20 transition"
    />
  </div>
);

export default Booking;
